/**
 * Geo-tree model for the Discover Communities catalog (DEV-1447, slice 2).
 *
 * Country → state → city drilldown. Pure data; no React. Tested.
 *
 * ── THE CARVE-OUT THAT MAKES THIS SUBTLE ──────────────────────────────────
 *
 * §7.5 omits inactive geos from the catalog's ANSWER SET — an inactive geo is a
 * location label, not a joinable community, so there is nothing to follow and
 * nothing to show on a shelf.
 *
 * But the tree is a NAVIGATION PATH, not an answer set. If an inactive state
 * were dropped from the tree, every active city beneath it would become
 * unreachable — you could not walk USA → (missing) → Hampton. So the tree keeps
 * inactive geos as **non-followable path segments**: visible, expandable,
 * selectable as a scope, but rendered as labels and never offered as something
 * to follow.
 *
 * Omission applies to the answer set. Never to the path. (§7.5)
 *
 * PRIVATE geos are a different case and ARE dropped from the tree entirely
 * (DEV-890): leaking the existence of a private community through a breadcrumb
 * is exactly the disclosure the policy forbids. If that orphans descendants,
 * they are unreachable via the tree — the safe failure, and vanishingly rare
 * for geography.
 */

import type { CommunityEntity } from '../../Components/CommunityCard/communityCardData';
import { isWorkshop } from '../../Data/Community/workshop';

export interface GeoNode {
  guid: string;            // upper-case
  name: string;
  entityType: string;
  /** Inactive geos are PATH SEGMENTS only — never followable. See header. */
  inactive: boolean;
  children: GeoNode[];
  /** Active, catalog-eligible communities in this node's subtree, incl. self. */
  subtreeCount: number;
}

/**
 * Eligible to appear in the TREE — looser than `isCatalogEligible`, and
 * deliberately so.
 *
 * Difference: the tree admits INACTIVE geos (as path segments). It still
 * excludes private (DEV-890), workshops (DEV-961) and the L4E sentinel.
 */
export function isTreeEligible(c: CommunityEntity): boolean {
  const etype = (c.entity_type || '').toLowerCase();
  if (!etype.startsWith('geo_')) return false;
  if (etype === 'platform_l4e') return false;
  if (c.visibility === 'private') return false;   // DEV-890 — never leak, not even as a path
  if (isWorkshop(c)) return false;                // DEV-961 / DEV-1483 — shared predicate
  return true;
}

/**
 * Build the geo forest.
 *
 * Roots = tree-eligible geos whose parent is absent from the eligible set
 * (Earth/geo_planet, or an entity orphaned because its parent was excluded).
 *
 * `countableGuids` — the guids that count toward `subtreeCount`. Pass the
 * catalog's active answer set, so an inactive path node still reports how many
 * real communities sit beneath it ("New Hampshire — 4"), which is exactly what
 * makes an inactive segment worth clicking.
 */
export function buildGeoTree(
  entities: CommunityEntity[] | undefined,
  parentMap: Map<string, string>,
  countableGuids: Set<string>,
): GeoNode[] {
  const all = entities || [];

  /** Every guid we know about, eligible or not — needed to tell a TRUE root
   *  (no parent at all, e.g. Earth) apart from an ORPHAN (parent exists in the
   *  data but was excluded from the tree). */
  const knownGuids = new Set(all.map((e) => e.entity_guid.toUpperCase()));
  const eligibleGuids = new Set(
    all.filter(isTreeEligible).map((e) => e.entity_guid.toUpperCase()),
  );

  /**
   * A node is admitted only if its ENTIRE ancestor chain is tree-eligible.
   *
   * Without this, excluding a private state (DEV-890) would leave its cities
   * parentless — and a parentless node becomes a ROOT, so "Hidden City" would
   * render as a top-level sibling of Earth. The private community's name never
   * leaks, but the structure is wrong and the disclosure is arguably worse:
   * a city surfacing at the root of the forest advertises that something was
   * removed above it.
   *
   * Dropping the orphan is the safe failure. Geography is a nearly-static
   * public hierarchy, so a private geo ancestor is vanishingly rare.
   */
  const hasEligibleAncestry = (guid: string): boolean => {
    let cur = parentMap.get(guid.toUpperCase());
    const seen = new Set<string>();
    for (let i = 0; i < 16 && cur && !seen.has(cur); i++) {
      seen.add(cur);
      if (!knownGuids.has(cur)) return true;      // parent unknown → treat as a true root
      if (!eligibleGuids.has(cur)) return false;  // parent known but EXCLUDED → orphan
      cur = parentMap.get(cur);
    }
    return true;
  };

  const eligible = all.filter(
    (e) => isTreeEligible(e) && hasEligibleAncestry(e.entity_guid),
  );

  const nodes = new Map<string, GeoNode>();
  for (const e of eligible) {
    const guid = e.entity_guid.toUpperCase();
    nodes.set(guid, {
      guid,
      name: e.name || '',
      entityType: (e.entity_type || '').toLowerCase(),
      inactive: e.is_active === false,
      children: [],
      subtreeCount: 0,
    });
  }

  const roots: GeoNode[] = [];
  for (const node of nodes.values()) {
    const parentGuid = parentMap.get(node.guid);
    const parent = parentGuid ? nodes.get(parentGuid) : undefined;
    if (parent && parent.guid !== node.guid) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Depth-first count + sort. Iterative, with a visited set: a cyclic
  // relationship row would otherwise hang the render.
  const counted = new Set<string>();
  const countSubtree = (node: GeoNode): number => {
    if (counted.has(node.guid)) return node.subtreeCount; // cycle guard
    counted.add(node.guid);
    let n = countableGuids.has(node.guid) ? 1 : 0;
    for (const child of node.children) n += countSubtree(child);
    node.subtreeCount = n;
    node.children.sort(
      (a, b) => b.subtreeCount - a.subtreeCount || a.name.localeCompare(b.name),
    );
    return n;
  };
  for (const r of roots) countSubtree(r);

  roots.sort((a, b) => b.subtreeCount - a.subtreeCount || a.name.localeCompare(b.name));

  // A path node with nothing real beneath it is noise — an empty label leading
  // to an empty page. Prune it. (A node that IS itself countable survives.)
  const prune = (list: GeoNode[]): GeoNode[] =>
    list
      .filter((n) => n.subtreeCount > 0)
      .map((n) => ({ ...n, children: prune(n.children) }));

  return prune(roots);
}

/** Every guid in `guid`'s subtree, including itself. Cycle-safe. */
export function subtreeGuids(root: GeoNode): Set<string> {
  const out = new Set<string>();
  const stack: GeoNode[] = [root];
  while (stack.length) {
    const n = stack.pop()!;
    if (out.has(n.guid)) continue;
    out.add(n.guid);
    for (const c of n.children) stack.push(c);
  }
  return out;
}

/** Find a node by guid anywhere in the forest. */
export function findNode(roots: GeoNode[], guid: string): GeoNode | null {
  const target = guid.toUpperCase();
  const stack = [...roots];
  const seen = new Set<string>();
  while (stack.length) {
    const n = stack.pop()!;
    if (seen.has(n.guid)) continue;
    seen.add(n.guid);
    if (n.guid === target) return n;
    for (const c of n.children) stack.push(c);
  }
  return null;
}

/** Root→node path, for the breadcrumb. Empty if not found. */
export function pathToNode(roots: GeoNode[], guid: string): GeoNode[] {
  const target = guid.toUpperCase();

  const walk = (node: GeoNode, trail: GeoNode[], seen: Set<string>): GeoNode[] | null => {
    if (seen.has(node.guid)) return null; // cycle guard
    seen.add(node.guid);
    const next = [...trail, node];
    if (node.guid === target) return next;
    for (const c of node.children) {
      const hit = walk(c, next, seen);
      if (hit) return hit;
    }
    return null;
  };

  for (const r of roots) {
    const hit = walk(r, [], new Set());
    if (hit) return hit;
  }
  return [];
}
